import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './AnalyticsCup.scss';

class FAQItem {
    constructor(question, answer) {
        this.question = question;
        this.answer = answer;
    }
}

class FAQSegment {
    constructor(title, items) {
        this.title = title;
        this.items = items;
    }
}

const FAQDropdown = ({ faqItem, itemNumber, isOpen, onToggle }) => {
    return (
        <div className="faq-item mb-3">
            <button 
                className={`faq-question ${isOpen ? 'active' : ''}`}
                onClick={onToggle}
                aria-expanded={isOpen}
            >
                <span><strong>{itemNumber}.</strong> {faqItem.question}</span>
                <span className="faq-icon">{isOpen ? '−' : '+'}</span>
            </button>
            <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                <div className="faq-answer-content">
                    {faqItem.answer}
                </div>
            </div>
        </div>
    );
};

export const AnalyticsCupFAQ = () => {
    const [openItem, setOpenItem] = useState(null);

    const faqSegments = [
        new FAQSegment(
            'General Questions',
            [
                new FAQItem(
                    "Can I submit as a team?",
                    <p>No, only individual submissions are allowed. Each participant must work independently on their project.</p>
                ),
                new FAQItem(
                    "What are the important dates?",
                    <>
                        <p>Key dates for the competition:</p>
                        <ul>
                            <li><strong>October 2025:</strong> Competition Kick-Off</li>
                            <li><strong>December 28th 2025:</strong> Submission Deadline</li>
                            <li><strong>January 8th 2026:</strong> Results Announced</li>
                            <li><strong>February 5th 2026:</strong> Invite-only Paris Finale at SkillCorner HQ</li>
                        </ul>
                    </>
                ),
                new FAQItem(
                    "How do I submit my project?",
                    <>
                        <p>Follow these steps:</p>
                        <ol>
                            <li>Use the official submission template for your chosen track:
                                <ul>
                                    <li><a href="https://github.com/PySport/analytics_cup_analyst" target="_blank" rel="noopener noreferrer">Analyst Track Template</a></li>
                                    <li><a href="https://github.com/PySport/analytics_cup_research" target="_blank" rel="noopener noreferrer">Research Track Template</a></li>
                                </ul>
                            </li>
                            <li>Submit your final project at <a href="https://pretalx.pysport.org" target="_blank" rel="noopener noreferrer">pretalx.pysport.org</a></li>
                        </ol>
                    </>
                ),
                new FAQItem(
                    "Can I submit to both tracks?",
                    <p>Yes! You can submit one project per track (Analyst and Research), giving you two opportunities to participate.</p>
                ),
                new FAQItem(
                    "What tools can I use?",
                    <p>You must use open-source tools only. This means no proprietary software like Excel, Tableau, PowerBI, etc. Your project should be reproducible using freely available open-source tools and libraries.</p>
                ),
                new FAQItem(
                    "What license should I use?",
                    <p>Your submission must use an open-source license such as MIT, Apache 2.0, or similar permissive licenses.</p>
                ),
                new FAQItem(
                    "How will projects be judged?",
                    <>
                        <p>Submissions are evaluated on four equally weighted criteria:</p>
                        <ul>
                            <li><strong>Innovation (25%):</strong> Novel approaches to sports analytics problems</li>
                            <li><strong>Technical Quality (25%):</strong> Code quality, architecture, and documentation</li>
                            <li><strong>Practical Impact (25%):</strong> Real-world applicability and value to the sports community</li>
                            <li><strong>Open Source Potential (25%):</strong> Likelihood of becoming a valuable open-source tool</li>
                        </ul>
                    </>
                ),
                new FAQItem(
                    "Do I need to use the SkillCorner data?",
                    <p>Yes, your submission must demonstrate use of the provided SkillCorner tracking data. This is a core requirement of the competition.</p>
                ),
                new FAQItem(
                    "Can I use code from other projects?",
                    <p>You must submit original work. While you can use open-source libraries and frameworks, the core analytical work and implementation must be your own. Plagiarized work will result in disqualification. Research should be properly cited.</p>
                ),
                new FAQItem(
                    "What if I miss the deadline?",
                    <p>Late submissions will not be accepted and will result in disqualification. Make sure to submit before midnight on December 28th 2025.</p>
                ),
                new FAQItem(
                    "How do I get support during the competition?",
                    <p>After sign-up you received a link to the Analytics Cup Discord server where you can ask questions, get clarifications, and connect with other participants.</p>
                ),
                new FAQItem(
                    "Who owns the intellectual property of my submission?",
                    <p>You retain ownership of your original contributions. However, your submission must use an open-source license, and by participating, you grant permission for SkillCorner and PySport to feature your work in case studies and promotional materials.</p>
                ),
                new FAQItem(
                    "Is there any way to get film associated with the tracking data?",
                    <p>No. </p>
                )
            ]
        ),
        new FAQSegment(
            'Open Source Projects',
            [
                new FAQItem(
                    "What is the Open Source Grant?",
                    <p>The winner receives €1,500 to develop their project into a fully-fledged Python package or significant pull request to an existing open-source project. The grant is released upon successful completion of the open-source deliverable.</p>
                ),
                new FAQItem(
                    "What qualifies as a good open-source deliverable?",
                    <p>A good open-source deliverable should include comprehensive documentation, unit tests, clear installation instructions, and follow best practices for Python package development. It should be useful to the broader sports analytics community and maintainable in the long term.</p>
                ),                
                new FAQItem(
                    "How do I get started with the Dynamic Events, Phases of Play and Physical Data?",
                    <p>You can follow the tutorial on <a href="https://github.com/SkillCorner/opendata/tree/master/resources/Tutorials" target="_blank" rel="noopener noreferrer">provided by SkillCorner.</a></p>
                ),
                new FAQItem(
                    "How do I get started with the tracking data?",
                    <p>You can follow the tutorial on <a href="https://github.com/SkillCorner/opendata/blob/master/resources/getting-started-skc-tracking-kloppy.ipynb" target="_blank" rel="noopener noreferrer">Getting Started with Kloppy and tracking data.</a></p>
                ),
                new FAQItem(
                    "Are there any existing open-source football analytics work I can use and draw inspiration from?",
                    <>
                        <p>Below is a <strong>non-exhaustive</strong> list of publicly available open source football analytics tools. Feel free to use these as inspiration, build on-top of these tools or develop them further. We advise Analytics Cup participants to not replicate existing work, but rather use it to build new open source tools instead.</p>

                        <p><strong>Plotting</strong></p>
                        <ul>
                            <li><a href="https://mplsoccer.readthedocs.io/en/latest/" target="_blank" rel="noopener noreferrer"><code>mplsoccer</code></a> and <a href="https://matplotlib.org/" target="_blank" rel="noopener noreferrer"><code>matplotlib</code></a> to plot some different configurations of tracking data.</li>
                            <li><a href="https://github.com/probberechts/d3-soccer" target="_blank" rel="noopener noreferrer"><code>soccer-d3</code></a></li>
                            <li>And check out <a href="https://kloppy.pysport.org/user-guide/getting-started/#exec-51--__tabbed_1_2" target="_blank" rel="noopener noreferrer">Kloppy's Plotting Examples</a></li>
                        </ul>

                        <p><strong>Resources for Positional Tracking Data</strong></p>
                        <ul>
                            <li><a href="https://floodlight.readthedocs.io/en/latest/" target="_blank" rel="noopener noreferrer">floodlight</a></li>
                            <li><a href="https://databallpy.readthedocs.io/en/latest/?badge=latest" target="_blank" rel="noopener noreferrer">databallpy</a></li>
                            <li><a href="https://github.com/hyunsungkim-ds" target="_blank" rel="noopener noreferrer">Hyunsung Kim @ KAIST</a></li>
                            <li><a href="https://github.com/Friends-of-Tracking-Data-FoTD" target="_blank" rel="noopener noreferrer">Friends of Tracking</a></li>
                            <li><a href="https://github.com/UnravelSports/UnravelSports" target="_blank" rel="noopener noreferrer">unravelsports</a></li>
                        </ul>

                        <p><strong>Other Football Analytics Resources</strong></p>
                        <ul>
                            <li><a href="https://socceraction.readthedocs.io/en/latest/" target="_blank" rel="noopener noreferrer">socceraction</a></li>
                            <li><a href="https://Footballdata.readthedocs.io/en/latest/" target="_blank" rel="noopener noreferrer">Footballdata</a></li>
                            <li><a href="https://github.com/ML-KULeuven/ETSY" target="_blank" rel="noopener noreferrer">ETSY</a></li>
                            <li><a href="https://github.com/ML-KULeuven/soccer_xg" target="_blank" rel="noopener noreferrer">soccer-xg</a></li>
                            <li><a href="https://openstarlab.readthedocs.io/en/latest/" target="_blank" rel="noopener noreferrer">OpenSTARLab</a></li>
                            <li><a href="https://github.com/devinpleuler/analytics-handbook" target="_blank" rel="noopener noreferrer">Soccer Analytics Handbook</a></li>
                            <li><a href="https://pypi.org/project/penaltyblog/" target="_blank" rel="noopener noreferrer">penaltyblog</a></li>
                        </ul>
                    </>
                )

            ]
        ),
        new FAQSegment(
            'Submission Specifics',
            [
                new FAQItem(
                    "What happens if my code doesn't run?",
                    <p>Your project must execute "out of the box" without errors. Submissions that do not run properly will be disqualified. Make sure to test your code thoroughly before submitting. Please be aware that different operating systems might require different procedures.</p>
                ),
                new FAQItem(
                    "What file formats are accepted for submissions?",
                    <p>Your submission should follow the provided GitHub template format, which includes Jupyter notebooks, Python scripts. All code should be submitted through the official pretalx submission portal with a link to your GitHub repository.</p>
                ),
                new FAQItem(
                    "How detailed does my submission need to be?",
                    <p>Make it as detailed as you like, but don't forget that it needs to be comprehensible too.</p>
                ),
                new FAQItem(
                    "Am I allowed to submit a Python package?",
                    <p>Yes, that is most certainly allowed. The Python package does not have to be contained within a single file. You are allowed to document <code>pip install my-package</code> in your submission - make sure to mention you created the package - and showcase its functionalities, and add the package to the requirements.</p>
                ),
                new FAQItem(
                    "Can I only submit a single file?",
                    <p>The requirements say you can put everything in the src folders, it says nothing about a single file. Only to showcase your work with a main.py or submission.ipynb. Clean up your code as much as possible and only put the really necessary things in the main.py or submissions.ipynb the rest you can import from the src folder. </p>
                )
            ]
        )
    ];

    const handleToggle = (itemId) => {
        setOpenItem(openItem === itemId ? null : itemId);
    };

    // Generate unique IDs for each item across all segments
    const getItemId = (segmentIndex, itemIndex) => {
        return `${segmentIndex}-${itemIndex}`;
    };

    // Generate item number (A1, A2, B1, etc.)
    const getItemNumber = (segmentIndex, itemIndex) => {
        const letter = String.fromCharCode(65 + segmentIndex); // 65 is 'A' in ASCII
        return `${letter}${itemIndex + 1}`;
    };

    return (
        <div className="analytics-cup">
            <Helmet>
                <title>FAQ — Analytics Cup | PySport</title>
            </Helmet>
            {/* Analytics Cup Header & Navigation */}
            <header className="analytics-header">
                <div className="container">
                    <div className="header-content">
                        <div className="brand-section">
                            <div className="brand-text">
                                <span className="brand-name">SkillCorner X PySport</span>
                                <span className="event-name">Analytics Cup</span>
                            </div>
                        </div>
                        <nav className="analytics-nav">
                            <div className="nav-links">
                                <a href="/analytics-cup" className="nav-link">Back to Analytics Cup</a>
                            </div>
                        </nav>
                    </div>
                </div>
            </header>

            {/* FAQ Content */}
            <section className="faq-content">
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="faq-header text-center mb-5">
                                <h1 className="page-title">Frequently Asked Questions</h1>
                                <p className="page-subtitle">Find answers to common questions about the Analytics Cup</p>
                            </div>

                            <div className="faq-segments">
                                {faqSegments.map((segment, segmentIndex) => (
                                    <div key={segmentIndex} className="faq-segment mb-5">
                                        <h2 className="segment-title">{segment.title}</h2>
                                        <div className="faq-list">
                                            {segment.items.map((faqItem, itemIndex) => {
                                                const itemId = getItemId(segmentIndex, itemIndex);
                                                const itemNumber = getItemNumber(segmentIndex, itemIndex);
                                                return (
                                                    <FAQDropdown
                                                        key={itemId}
                                                        faqItem={faqItem}
                                                        itemNumber={itemNumber}
                                                        isOpen={openItem === itemId}
                                                        onToggle={() => handleToggle(itemId)}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="faq-footer text-center mt-5 pt-4 border-top">
                                <p><strong>Still have questions?</strong></p>
                                <p>Join our Discord server for support and updates.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};